import { HttpError } from '@/errors';
import type { ISessionClient, LiveDescriptor, LiveProvider, SessionMeta } from '@/types';

const LIVE_PROVIDERS: readonly LiveProvider[] = ['ws', 'socket.io', 'pusher'];

/** Narrows an untrusted response field to a valid `LiveDescriptor` or undefined. */
function parseLiveDescriptor(raw: unknown): LiveDescriptor | undefined {
  const provider = (raw as { provider?: unknown } | null | undefined)?.provider;
  return typeof provider === 'string' && LIVE_PROVIDERS.includes(provider as LiveProvider)
    ? { provider: provider as LiveProvider }
    : undefined;
}

/**
 * Creates recording sessions on the Cobrowsing backend and fetches session metadata.
 * Single responsibility: one HTTP call, one return value.
 */
export class SessionClient implements ISessionClient {
  /**
   * Transport advertised by the backend on the latest create/get call. The
   * backend reports the same value for every session (it's a server-wide
   * setting), so the recorder can read it right after creating or resuming a
   * session. `undefined` until the first call, or if the backend omits it.
   */
  liveDescriptor?: LiveDescriptor;

  constructor(
    private readonly apiUrl: string,
    private readonly apiKey: string,
    private readonly csrfToken?: string
  ) {}

  async createSession(meta: Omit<SessionMeta, 'sessionId'>): Promise<string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-api-key': this.apiKey,
    };
    if (this.csrfToken) {
      headers['x-csrf-token'] = this.csrfToken;
    }

    // Only send what the backend's createSessionSchema accepts. The server
    // derives userAgent authoritatively from the request header, so passing it
    // in the body is redundant — keep `meta.userAgent` for our own bookkeeping
    // (session-resume comparison) but don't put it on the wire.
    //
    // `userId` is the visitor id — caller-supplied for logged-in users, or
    // the recorder's `cnv_<uuid>` localStorage id for anonymous visitors. The
    // backend uses it as `Session.userId` so the per-agent watchlist (and
    // dashboard `userId` filter) can key off a single stable identifier.
    const reqBody: Record<string, unknown> = { url: meta.url };
    if (meta.userId) reqBody.userId = meta.userId;
    const res = await fetch(`${this.apiUrl}/api/sessions`, {
      method: 'POST',
      headers,
      body: JSON.stringify(reqBody),
    });

    if (!res.ok) {
      throw new HttpError(
        res.status,
        `[SessionClient] Failed to create session: ${res.status} ${res.statusText}`
      );
    }

    const body = await res.json();

    if (typeof body?.data?.sessionId !== 'string') {
      throw new Error('[SessionClient] Unexpected response shape — missing data.sessionId');
    }

    const live = parseLiveDescriptor(body.data.live);
    if (live) this.liveDescriptor = live;

    return body.data.sessionId as string;
  }

  async getSession(sessionId: string): Promise<SessionMeta> {
    const headers: Record<string, string> = {
      'x-api-key': this.apiKey,
    };
    if (this.csrfToken) {
      headers['x-csrf-token'] = this.csrfToken;
    }

    // Metadata-only, API-key-scoped endpoint. The full `GET /api/sessions/:id`
    // is dashboard-only (cloud / login-cookie auth) and returns events, which
    // the recorder neither has credentials for nor needs.
    const res = await fetch(`${this.apiUrl}/api/sessions/${sessionId}/meta`, {
      method: 'GET',
      headers,
    });

    if (!res.ok) {
      throw new HttpError(
        res.status,
        `[SessionClient] Failed to get session: ${res.status} ${res.statusText}`
      );
    }

    const body = await res.json();
    const session = body?.data?.session;
    if (typeof session?.id !== 'string') {
      throw new Error('[SessionClient] Unexpected response shape — missing data.session.id');
    }
    const live = parseLiveDescriptor(body.data.live);
    if (live) this.liveDescriptor = live;
    return {
      sessionId: session.id,
      url: typeof session.url === 'string' ? session.url : '',
      userAgent: typeof session.userAgent === 'string' ? session.userAgent : '',
    };
  }
}
